import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import makeStyles from "@material-ui/core/styles/makeStyles";
import ActionCancel from "@material-ui/icons/Cancel";

import {
  PageHeader,
  PageFooter,
  Button,
  SimpleForm,
  TextInput,
  SaveButton,
  changePassword,
  ReferenceArrayField,
  SelectInput,
  changeLocale,
  translate as translateHOC
} from "jazasoft";

import SingleFieldListCsv from "../../components/SingleFieldListCsv";
import { Role, Locale } from "../../utils/types";
import { authUrl, appId } from "../../App";
import { isEmpty, listFromCsv } from "../../utils/helpers";

const LocaleName = {
  en: "English",
  hi: "हिंदी"
  // ta: "தமிழ்",
  // kn: "ಕನ್ನಡ",
  // te: "తెలుగు",
  // bn: "বাংলা",
  // ml: "മലയാളം",
  // mr: "मराठी",
};

function isValidPassword(password) {
  if (isEmpty(password)) return false;

  if (password.length > 15) {
    return false;
  }

  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;
  return regex.test(password);
}

const validate = (values, props, requireStrongPassword) => {
  const { translate } = props;
  const errors = {};
  const messageSource = "resources.profile.message";
  if (!values.oldPassword) {
    errors.oldPassword = translate(`${messageSource}.old_password_required`, { _: "Old Password Required." });
  }

  if (!values.newPassword) {
    errors.newPassword = translate(`${messageSource}.new_password_required`, { _: "New Password Required." });
  } else if (values.newPassword.length < 8) {
    errors.newPassword = translate(`${messageSource}.password_is_too_short`, {
      _: "The password is too short. It must be atleast 8 characters"
    });
  } else if (values.newPassword.length > 15) {
    errors.newPassword = translate(`${messageSource}.password_is_too_long`, {
      _: "The password is too Long. It must be less then 15 characters"
    });
  }

  if (!values.confirmNewPassword) {
    errors.confirmNewPassword = translate(`${messageSource}.confirm_new_password_required`, {
      _: "Confirm New Password Required."
    });
  }

  if (values.newPassword !== values.confirmNewPassword) {
    errors.confirmNewPassword = translate(`${messageSource}.password_do_not_match`, { _: "Password do not match." });
  }

  if (!isEmpty(values.confirmNewPassword) && requireStrongPassword === true && !isValidPassword(values.confirmNewPassword)) {
    errors.confirmNewPassword = translate(`${messageSource}.valid_password`, {
      _: "Password must be between 8 and 15 characters and include at least one uppercase letter, one lowercase letter, one number, and one special character (e.g., Demo@843)"
    });
  }

  return errors;
};

const useStyles = makeStyles(theme => ({
  content: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  }
}));

const FormFooter = ({ onCancel, ...props }) => (
  <PageFooter>
    <SaveButton label="js.action.submit" {...props} />
    <Button label="js.action.cancel" variant="contained" onClick={onCancel}>
      <ActionCancel />
    </Button>
  </PageFooter>
);

const Profile = props => {
  const { translate } = props;
  // state
  const [dialogState, setDialogState] = useState({ dialogActive: false, dialogName: null });
  const [requireStrongPassword, setRequireStrongPassword] = useState(null);
  const { dialogActive, dialogName } = dialogState;

  const authState = useSelector(state => state.jazasoft.auth);
  const locale = useSelector(state => state.i18n.locale);
  const username = authState.username;

  const closeDialog = () => {
    setDialogState({ dialogActive: false, dialogName: null });
  };

  //hooks
  const disptach = useDispatch();
  const classes = useStyles();

  useEffect(() => {
    if (requireStrongPassword === null && !isEmpty(username)) {
      axios.get(`${authUrl}/api/users/check`, { params: { username, appId } }).then(response => {
        console.log({ response });
        setRequireStrongPassword(response.data?.requireStrongPassword);
      });
    }
  }, [username, requireStrongPassword]);

  const onSubmit = values => {
    if (dialogName === "forgot_password") {
      const { username, clientId } = authState;
      disptach(changePassword({ ...values, username, tenantId: clientId }));
      closeDialog();
    } else if (dialogName === "change_locale") {
      disptach(changeLocale(values.locale, authState?.tenant?.tenantId));
    }
    closeDialog();
  };

  const unitList = authState?.tenant?.unitList || [];
  const unitIds = listFromCsv(authState?.units);
  const units = !isEmpty(unitList) ? unitList.filter(u => unitIds.includes(u.unitId)).map(e => e.name) : unitIds;

  let data = [
    { label: "Full Name", value: authState.fullName },
    { label: "User Name", value: authState.username },
    { label: "Email", value: authState.email || "-" },
    { label: "Mobile", value: authState.mobile },
    { label: "Language", value: LocaleName[locale] || Locale[locale] },
    { label: "Unit(s)", value: units.join(", ") },
    {
      label: "Role(s)",
      value: authState.roleList
        .filter(r => r.role?.roleId === Role.MASTER || r.role?.roleId === Role.SUPER_USER || r.applicationId === appId)
        .map(r => r.role?.name)
        .join(", ")
    }
  ];

  const customerIds = authState && authState.customerId && authState.customerId.map(Number);
  if (customerIds && customerIds.length > 0) {
    data.push({
      label: "Customer Access",
      value: (
        <span>
          <ReferenceArrayField record={{ customerIds: customerIds }} source="customerIds" reference="customers">
            <SingleFieldListCsv source="name" />
          </ReferenceArrayField>
        </span>
      )
    });
  }

  data = data.map(e => ({ ...e, label: translate(`resources.profile.fields.${e.label}`, { _: e.label }) }));

  const choicesLocale = Object.keys(Locale).map(key => ({
    id: key,
    name: LocaleName[key] || Locale[key]
  }));

  return (
    <div>
      <PageHeader title="resources.profile.pageHeader.my_profile" titleArgs={{ _: "My Profile" }} />
      <Card className={classes.content} elavation={4} square={true}>
        <Grid container>
          <Grid item xs={12} sm={8}>
            <List>
              {data.length > 0 &&
                data.map(e => (
                  <ListItem key={e.label} button>
                    <ListItemText secondary={e.label} secondaryTypographyProps={{ variant: "subtitle1" }} />
                    <ListItemSecondaryAction>
                      <Typography component="div">{e.value}</Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          </Grid>
        </Grid>

        <PageFooter>
          <Button
            variant="contained"
            color="primary"
            label={translate("resources.profile.button.change_paasword", { _: "Change Password" })}
            onClick={() =>
              setDialogState({
                dialogActive: true,
                dialogName: "forgot_password"
              })
            }
          />
          <Button
            style={{ marginLeft: "1em" }}
            variant="outlined"
            color="primary"
            label={translate("resources.profile.button.change_language", { _: "Change Language" })}
            onClick={() =>
              setDialogState({
                dialogActive: true,
                dialogName: "change_locale"
              })
            }
          />
        </PageFooter>
      </Card>

      <div>
        <Dialog open={dialogActive} onClose={closeDialog} aria-labelledby="form-dialog-title">
          <DialogTitle id="form-dialog-title">
            {dialogName === "forgot_password"
              ? translate("resources.profile.title.change_paasword", { _: "Change Password" })
              : translate("resources.profile.title.change_language", { _: "Change Language" })}
          </DialogTitle>
          <DialogContent style={{ width: 400 }}>
            <SimpleForm
              onSubmit={onSubmit}
              validate={(values, props) => validate(values, props, requireStrongPassword)}
              record={{ locale }}
              i18nKey="profile"
              footer={<FormFooter onCancel={closeDialog} />}>
              {dialogName === "forgot_password" && <TextInput source="oldPassword" type="password" xs={12} fullWidth options={{ fullWidth: true }} />}
              {dialogName === "forgot_password" && <TextInput source="newPassword" type="password" xs={12} fullWidth options={{ fullWidth: true }} />}
              {dialogName === "forgot_password" && (
                <TextInput source="confirmNewPassword" type="password" xs={12} fullWidth options={{ fullWidth: true }} />
              )}
              {dialogName === "change_locale" && (
                <SelectInput source="locale" xs={12} fullWidth options={{ fullWidth: true }} choices={choicesLocale} />
              )}
            </SimpleForm>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default translateHOC(Profile);
